import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, CheckCircle2, ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/lib/supabase';
import { canViewCosts as canViewCostsRole } from '@/lib/roles';
import {
  formatDate, formatDateTime, formatCurrency,
  calcLunchBreaksMs, cn,
} from '@/lib/utils';
import { getActiveDaysIfUnmanaged } from '@/lib/api/techActivity';
import {
  buildStageTimeline, resolveTimelineOrderEnd, assessBuildDuration, earliestStressStart,
  type StageRow, type BuildDuration,
} from '@/lib/stageTimeline';
import { BuildDurationValue, BuildDurationNote, PausedTimeNote } from '@/components/BuildDuration';
import { PauseReviewList } from '@/components/BuildPauses';
import { BuildAttribution } from '@/components/BuildAttribution';
import { fetchOrderPauses } from '@/lib/api/pauses';
import { countedPauses, orderPauseIntervals, unitPauseIntervals, type PauseRow } from '@/lib/pauses';
import { formatDurationMs } from '@/lib/stageTimeline';

type Span = { start: string | null; end: string | null };
import { PrintBuildCertificate } from '@/components/PrintBuildCertificate';
import { fetchSalesForSerials, normaliseSerial, type SerialSale } from '@/lib/api/salesEntries';

/* ── Stage / Stepper constants ─────────────────────────────────── */

import { PIPELINE_STATUSES } from '@/lib/assemblyOrderStatus';
import { fetchStressTests } from '@/lib/api/stressTests';
import { StressTestUpload } from '@/components/StressTestUpload';
import { useCurrentUser } from '@/lib/useCurrentUser';

const FLOW_LABELS: Record<string, string> = {
  pending: 'Pending',
  building: 'Building',
  software: 'Software',
  testing: 'Testing',
  awaiting_review: 'Review',
};

const TYPE_LABELS: Record<string, string> = {
  CPU: 'CPU', FAN: 'CPU Cooler', MB: 'Motherboard', RAM: 'RAM',
  ROM: 'Storage', GPU: 'Graphics', PSU: 'PSU', OS: 'OS',
  CAS: 'Case', ACC: 'Accessory', COO: 'Cooler',
};
function getTypeLabel(type: string): string {
  if (TYPE_LABELS[type]) return TYPE_LABELS[type];
  if (type?.startsWith('ROM')) return 'Storage';
  if (type?.startsWith('ACC')) return 'Accessory';
  return type;
}
import { getComponentSortRank } from '@/types/database';
function compSort(type: string): number {
  return getComponentSortRank(type ?? '');
}

/* ── Helpers ───────────────────────────────────────────────────── */

function calculateWorkdays(start: Date, end: Date): number {
  let count = 0;
  const cur = new Date(start);
  cur.setHours(0, 0, 0, 0);
  const e = new Date(end);
  e.setHours(0, 0, 0, 0);
  while (cur <= e) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{children || '—'}</dd>
    </div>
  );
}

/* ── Main Page ─────────────────────────────────────────────────── */

export function PAODetailPage() {
  const { id } = useParams<{ id: string }>();

  /* 0. Role check for cost visibility */
  const { data: myRole } = useQuery({
    queryKey: ['pao-detail-role'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data } = await supabase.from('technicians').select('role').eq('user_id', user.id).single();
      return data?.role ?? null;
    },
  });
  // Was `myRole !== 'technician'`. Same people in practice - this route admits
  // only super_admin, admin, manager and technician - but stated as an
  // allow-list so it does not quietly widen if that guard changes.
  const canViewCosts = canViewCostsRole(myRole);

  const { data: currentUser } = useCurrentUser();

  /* 1. Order + joins */
  const { data: order, isLoading } = useQuery({
    queryKey: ['pao-detail', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('assembly_orders')
        .select('*, review_remarks_author:technicians!review_remarks_by(name, alias)')
        .eq('id', id!)
        .single();
      if (error) throw error;

      // Fetch build description
      let buildDescription = '';
      if (data.build_sku) {
        const { data: build } = await supabase
          .from('builds')
          .select('description')
          .eq('sku', data.build_sku)
          .maybeSingle();
        buildDescription = build?.description || '';
      }
      // build_type lives on assembly_orders, not builds
      const buildType = data.build_type || '';

      // Fetch technician name
      let techName = '—';
      if (data.technician_id) {
        const { data: tech } = await supabase
          .from('technicians')
          .select('name, alias')
          .eq('id', data.technician_id)
          .single();
        techName = tech?.alias || tech?.name || '—';
      }

      // Fetch reservation code
      let reservationCode: string | null = null;
      if (data.reservation_id) {
        const { data: res } = await supabase
          .from('reservations')
          .select('reservation_code')
          .eq('id', data.reservation_id)
          .maybeSingle();
        reservationCode = res?.reservation_code || null;
      }

      // Stage timeline (async for unmanaged tech). Active days are fetched
      // from order creation, not the first stage tick: a stress test can
      // predate every recorded tick, and the duration is then measured from it.
      const ts = data.stage_timestamps || {};
      let activeDays: Set<string> | undefined;
      if (data.technician_id && data.created_at) {
        activeDays = await getActiveDaysIfUnmanaged(data.technician_id, new Date(data.created_at), new Date());
      }

      function calcSpan(rows: StageRow[]): Span {
        const start = rows[0]?.started ?? null;
        const end = [...rows].reverse().find(r => r.completed)?.completed ?? null;
        return { start, end };
      }

      function calcLunchMs(rows: StageRow[]): number {
        const first = rows[0]?.started;
        const last = [...rows].reverse().find(r => r.completed)?.completed;
        if (!first || !last) return 0;
        return calcLunchBreaksMs(new Date(first), new Date(last), activeDays);
      }

      // Authoritative order bounds: "Start Building" click on one end, and
      // the moment the technician actually finished on the other. For Posted
      // orders `completed_at` is the admin-approval time (not the tech's
      // finish time), so we resolve via tech_signed_at → latest stage
      // completion → completed_at → completed_date.
      const orderStart = data.started_at || null;
      const orderEnd = resolveTimelineOrderEnd(data, ts);

      // Pauses: a unit's own plus whole-order ones come off that unit; only
      // time when every unit was paused comes off the order (orderPauseIntervals).
      // Rejected pauses count as work.
      const unitCount = data.quantity || 1;
      const pauses: PauseRow[] = await fetchOrderPauses(data.id).catch(() => []);
      const counted = countedPauses(pauses);
      const orderPauses = orderPauseIntervals(counted, unitCount);

      // Order-level stages (all-units aggregate)
      const orderStages = buildStageTimeline(ts, { orderStart, orderEnd, activeDays, pauses: orderPauses });

      // Per-unit stages — each unit's span is independent, based on its own timestamps
      const unitsTs: Record<string, Record<string, any>> = ts.units || {};
      const perUnitStages: Record<string, { stages: StageRow[]; span: Span; lunchMs: number }> = {};
      for (let i = 1; i <= unitCount; i++) {
        const k = String(i);
        const src = unitsTs[k];
        if (src && Object.keys(src).length > 0) {
          const rows = buildStageTimeline(src, { orderStart, orderEnd, activeDays, pauses: unitPauseIntervals(counted, i) });
          perUnitStages[k] = { stages: rows, span: calcSpan(rows), lunchMs: calcLunchMs(rows) };
        }
      }
      // If no per-unit data exists (legacy orders / button-only flow), fall back
      // to the order-level rows for unit 1 so the timeline is never empty.
      const hasPerUnit = Object.keys(perUnitStages).length > 0;
      if (!hasPerUnit && orderStages.length > 0) {
        perUnitStages['1'] = { stages: orderStages, span: calcSpan(orderStages), lunchMs: calcLunchMs(orderStages) };
      }

      // When per-unit data exists, derive the order span from actual unit timestamps
      // (earliest unit start → latest unit completion) instead of order-level aggregates
      let orderSpan = calcSpan(orderStages);
      if (hasPerUnit) {
        const starts = Object.values(perUnitStages).map(u => u.span.start).filter((s): s is string => !!s);
        const ends = Object.values(perUnitStages).map(u => u.span.end).filter((s): s is string => !!s);
        if (starts.length && ends.length) {
          orderSpan = {
            start: starts.reduce((a, b) => (new Date(a) < new Date(b) ? a : b)),
            end: ends.reduce((a, b) => (new Date(a) > new Date(b) ? a : b)),
          };
        }
      }

      // Days to build / process
      const createdDate = data.created_at ? new Date(data.created_at) : null;
      const completedDate = data.completed_date ? new Date(data.completed_date) : null;
      const daysToBuild = createdDate && completedDate && completedDate >= createdDate
        ? calculateWorkdays(createdDate, completedDate)
        : null;

      return {
        ...data,
        buildDescription,
        buildType,
        techName,
        reservationCode,
        orderStages,
        perUnitStages,
        daysToBuild,
        orderSpan,
        activeDays,
        unitCount,
        pauses,
        orderPauses,
      };
    },
    enabled: !!id,
  });

  /* 1b. What happened to these units after they left — keyed on serial.
     Via RPC, not sales_entries: the table is admin-only but this page is not,
     so a direct query would show managers and technicians a false "never
     sold". Date, document and location only; never revenue or cost. */
  const orderSerials = useMemo(() => {
    if (!order) return [];
    const sns = (order.serial_numbers as Record<string, string> | null) ?? {};
    const fromMap = Object.entries(sns).filter(([, v]) => v);
    if (fromMap.length) return fromMap.map(([unit, sn]) => ({ unit, serial: sn }));
    return order.serial_number ? [{ unit: '1', serial: order.serial_number as string }] : [];
  }, [order]);

  const { data: unitSales } = useQuery({
    queryKey: ['pao-detail-sales', orderSerials.map(s => s.serial).sort().join('|')],
    queryFn: () => fetchSalesForSerials(orderSerials.map(s => s.serial)),
    enabled: orderSerials.length > 0,
  });

  /** Sales grouped by the serial they belong to, oldest first. */
  const salesByUnit = useMemo(() => {
    const bySerial = new Map<string, SerialSale[]>();
    for (const sale of unitSales ?? []) {
      const key = normaliseSerial(sale.serial_no);
      const list = bySerial.get(key);
      if (list) list.push(sale);
      else bySerial.set(key, [sale]);
    }
    return orderSerials
      .map(({ unit, serial }) => ({ unit, serial, sales: bySerial.get(normaliseSerial(serial)) ?? [] }))
      .filter(u => u.sales.length > 0);
  }, [unitSales, orderSerials]);

  /* 2. Components */
  const { data: components } = useQuery({
    queryKey: ['pao-detail-components', id],
    queryFn: async () => {
      const { data: comps } = await supabase
        .from('assembly_order_components')
        .select('*')
        .eq('ao_id', id!);
      if (!comps?.length) return [];

      // Fetch descriptions
      const skus = [...new Set(comps.map(c => c.component_sku).filter(Boolean))];
      const descMap: Record<string, string> = {};
      if (skus.length) {
        const { data: descs } = await supabase
          .from('components')
          .select('part_number, description')
          .in('part_number', skus);
        descs?.forEach(d => { descMap[d.part_number] = d.description; });
      }

      const aoQty = order?.quantity || 1;
      return comps.map(c => {
        const totalQty = c.component_qty || 0;
        const perBuildQty = totalQty / aoQty;
        const unitCost = c.cost_unit_ex_vat || 0;
        return {
          type: c.component_type || 'COMPONENT',
          partNumber: c.component_sku || '',
          description: descMap[c.component_sku] || c.component_sku || '',
          quantity: perBuildQty,
          unitCost,
          lineTotal: perBuildQty * unitCost,
        };
      });
    },
    enabled: !!id && !!order,
  });

  /* 3. Benchmarks */
  const { data: benchmarks } = useQuery({
    queryKey: ['pao-detail-benchmarks', id],
    queryFn: async () => {
      const { data } = await supabase
        .from('assembly_order_benchmarks')
        .select('*')
        .eq('assembly_order_id', id!)
        .order('unit_number', { ascending: true });
      return data || [];
    },
    enabled: !!id,
  });

  /* 3b. Stress tests */
  const { data: stressTests = [] } = useQuery({
    queryKey: ['stress-tests', id],
    queryFn: () => fetchStressTests(id!),
    enabled: !!id,
  });

  /* 4. Notes */
  const { data: notes } = useQuery({
    queryKey: ['pao-detail-notes', id],
    queryFn: async () => {
      const { data } = await supabase
        .from('assembly_order_notes')
        .select(`id, note_text, created_at, author:technicians!created_by(name, alias, role)`)
        .eq('ao_id', id!)
        .order('created_at', { ascending: true });
      return data || [];
    },
    enabled: !!id,
  });

  /* 5. Audit trail */
  const { data: auditLogs } = useQuery({
    queryKey: ['pao-detail-audit', id],
    queryFn: async () => {
      const { data } = await supabase
        .from('audit_log')
        .select('*')
        .eq('entity_id', id!)
        .eq('entity_type', 'assembly_order')
        .order('created_at', { ascending: false });
      return data || [];
    },
    enabled: !!id,
  });

  /* Durations, judged against the stress tests: a unit whose log predates
     every recorded tick is measured from the log instead (see assessBuildDuration). */
  const durations = useMemo(() => {
    if (!order) return null;
    const perUnit: Record<string, BuildDuration> = {};
    const counted = countedPauses(order.pauses as PauseRow[]);
    for (const [k, unit] of Object.entries(order.perUnitStages as Record<string, { span: Span }>)) {
      const unitStress = stressTests.find(t => String(t.unit_number) === k)?.started_at;
      perUnit[k] = assessBuildDuration(
        unit.span.start, unit.span.end, unitStress, order.activeDays, unitPauseIntervals(counted, Number(k)),
      );
    }
    const orderDuration = assessBuildDuration(
      order.orderSpan.start, order.orderSpan.end, earliestStressStart(stressTests), order.activeDays, order.orderPauses,
    );
    return { perUnit, order: orderDuration };
  }, [order, stressTests]);

  const perBuildCost = (order?.total_cost_ex_vat || 0) / (order?.quantity || 1);
  const [activeTimelineUnit, setActiveTimelineUnit] = useState('1');
  const [auditOpen, setAuditOpen] = useState(false);

  /* ── Loading / Not found ──────────────────────────────────────── */

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/stock-io"><ArrowLeft className="h-4 w-4 mr-2" />Back to Posted Orders</Link>
        </Button>
        <p className="text-muted-foreground">Order not found.</p>
      </div>
    );
  }

  /* ── Render ───────────────────────────────────────────────────── */

  return (
    <div className="space-y-6">
      {/* ─── 1. Header ─────────────────────────────────────────── */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" asChild className="mt-1">
          <Link to="/stock-io"><ArrowLeft className="h-5 w-5" /></Link>
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight font-mono">{order.order_number}</h1>
            {order.build_sku && (
              <span className="text-lg text-muted-foreground font-mono">{order.build_sku}</span>
            )}
            <div className="ml-auto flex items-center gap-2">
              <PrintBuildCertificate
                order={{
                  ...order,
                  build: { id: order.build_id, sku: order.build_sku, description: order.buildDescription },
                }}
                components={components?.map(c => ({
                  sku: c.partNumber,
                  type: c.type,
                  typeLabel: TYPE_LABELS[c.type] ?? c.type,
                  description: c.description,
                  qty: c.quantity,
                  unit_cost: c.unitCost,
                  line_total: c.lineTotal,
                }))}
                benchmarks={benchmarks?.reduce((acc, b: any) => {
                  if (b.passmark_rating) acc[String(b.unit_number)] = {
                    passmark_rating: b.passmark_rating,
                    cpu_mark: b.cpu_mark,
                    graphics_3d_mark: b.graphics_3d_mark,
                    memory_mark: b.memory_mark,
                    disk_mark: b.disk_mark,
                  };
                  return acc;
                }, {} as Record<string, any>)}
                variant="outline"
                size="sm"
              />
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Posted
              </span>
            </div>
          </div>
          {order.buildDescription && (
            <p className="text-muted-foreground mt-0.5">{order.buildDescription}</p>
          )}
        </div>
      </div>

      {/* ─── 2. Order Summary ──────────────────────────────────── */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-x-6 gap-y-4">
            <KV label="Order Number"><span className="font-mono">{order.order_number}</span></KV>
            <KV label="Build SKU"><span className="font-mono">{order.build_sku || '—'}</span></KV>
            <KV label="Build Type">{order.buildType || '—'}</KV>
            <KV label="Quantity">{order.quantity || 1}</KV>
            {canViewCosts && <KV label="Build Cost">{formatCurrency(perBuildCost)}</KV>}

            <KV label="Technician">{order.techName}</KV>
            <KV label="Reservation">
              {order.reservation_id ? (
                canViewCosts ? (
                  <Link to={`/reservations/${order.reservation_id}`} className="text-primary underline font-mono">
                    {order.reservationCode || order.reservation_id.slice(0, 8)}
                  </Link>
                ) : (
                  <span className="font-mono">{order.reservationCode || order.reservation_id.slice(0, 8)}</span>
                )
              ) : '—'}
            </KV>
            <KV label="Transfer Order"><span className="font-mono">{order.to_number || '—'}</span></KV>
            {(() => {
              const sns = order.serial_numbers as Record<string, string> | null;
              const entries = sns ? Object.entries(sns).filter(([, v]) => v) : [];
              if (entries.length > 1) {
                return (
                  <KV label="Serial Numbers">
                    <div className="space-y-0.5">
                      {entries.map(([unit, sn]) => (
                        <div key={unit} className="font-mono text-sm">
                          <span className="text-muted-foreground text-xs">#{unit}</span> {sn}
                        </div>
                      ))}
                    </div>
                  </KV>
                );
              }
              const single = entries.length === 1 ? entries[0][1] : (order.serial_number || '—');
              return <KV label="Serial Number"><span className="font-mono">{single}</span></KV>;
            })()}
            {canViewCosts && <KV label="Total Cost">{order.quantity > 1 ? formatCurrency(order.total_cost_ex_vat) : '—'}</KV>}

            <KV label="Created">{formatDate(order.created_at)}</KV>
            <KV label="Started">{formatDate(order.started_at || order.created_at)}</KV>
            <KV label="Completed">{formatDate(order.completed_date)}</KV>
            <KV label="Days to Build">{order.daysToBuild != null ? `${order.daysToBuild} day${order.daysToBuild !== 1 ? 's' : ''}` : '—'}</KV>
            <KV label="Order Duration">
              <BuildDurationValue duration={durations?.order} gross />
              <BuildDurationNote duration={durations?.order} />
            </KV>
            {!!durations?.order.pausedMs && durations.order.kind !== 'untracked' && (
              <KV label="Assembly Duration">
                <BuildDurationValue duration={durations.order} />
                <p className="text-xs text-muted-foreground">{formatDurationMs(durations.order.pausedMs)} paused, not counted</p>
              </KV>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ─── 2b. Sold ──────────────────────────────────────────── */}
      {salesByUnit.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <h2 className="text-sm font-semibold mb-3">Sold</h2>
            <div className="space-y-3">
              {salesByUnit.map(({ unit, serial, sales }) => (
                <div key={unit} className="text-sm">
                  {orderSerials.length > 1 && (
                    <div className="text-xs text-muted-foreground mb-1">
                      Unit #{unit} · <span className="font-mono">{serial}</span>
                    </div>
                  )}
                  <div className="space-y-0.5">
                    {/* A returned unit is resold under the same serial, so this is
                        a history rather than a single event. */}
                    {sales.map((s, i) => (
                      <div key={`${s.document_no}-${i}`} className="flex flex-wrap items-baseline gap-x-2">
                        <span className={cn('font-medium', s.is_return && 'text-amber-600 dark:text-amber-500')}>
                          {s.is_return ? 'Returned' : 'Sold'}
                        </span>
                        <span>{formatDate(s.posting_date)}</span>
                        <span className="text-muted-foreground font-mono text-xs">{s.document_no}</span>
                        <span className="text-muted-foreground text-xs">{s.location_code}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── 3. Workflow Stepper ───────────────────────────────── */}
      <Card>
        <CardContent className="pt-6 pb-4">
          <div className="flex items-center gap-1 px-2">
            {PIPELINE_STATUSES.map((step, idx, arr) => {
              return (
                <div key={step} className="flex items-center flex-1">
                  <div className="flex flex-col items-center gap-1 flex-1">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center border-2 bg-green-100 text-green-700 border-green-300">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                    <span className="text-[10px] leading-tight text-center font-medium text-muted-foreground">
                      {FLOW_LABELS[step]}
                    </span>
                  </div>
                  {idx < arr.length - 1 && (
                    <div className="h-0.5 flex-1 mx-1 rounded mb-4 bg-green-300" />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ─── 4. Build Timeline ─────────────────────────────────── */}
      {Object.keys(order.perUnitStages).length > 0 && (() => {
        const unitKeys = Object.keys(order.perUnitStages).sort((a, b) => Number(a) - Number(b));
        const activeKey = unitKeys.includes(activeTimelineUnit) ? activeTimelineUnit : unitKeys[0];
        const active = order.perUnitStages[activeKey];
        const activeDuration = durations?.perUnit[activeKey];
        const isMultiUnit = order.unitCount > 1;

        return (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Order Timeline</p>
                {isMultiUnit && order.orderSpan.start && order.orderSpan.end && (
                  <span className="text-xs text-muted-foreground">
                    Order total: <BuildDurationValue duration={durations?.order} className="font-medium text-foreground" />
                  </span>
                )}
              </div>

              {/* Unit tabs */}
              {isMultiUnit && (
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-medium text-muted-foreground">Unit:</span>
                  <div className="flex gap-1">
                    {unitKeys.map(k => (
                      <button
                        key={k}
                        onClick={() => setActiveTimelineUnit(k)}
                        className={cn(
                          'px-3 py-1 text-xs font-medium rounded-md border transition-colors',
                          activeKey === k
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background hover:bg-muted border-border'
                        )}
                      >
                        Unit {k}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {active?.stages?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left p-2 font-medium">Stage</th>
                        <th className="text-left p-2 font-medium">Started</th>
                        <th className="text-left p-2 font-medium">Completed</th>
                        <th className="text-right p-2 font-medium">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {active.stages.map((s: any, i: number) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="p-2">
                            {s.stage}
                            {s.estimated && (
                              <span className="ml-1.5 text-[10px] uppercase tracking-wide text-muted-foreground" title="Estimated from section weights — no explicit checklist timestamps recorded">
                                est.
                              </span>
                            )}
                            {!!s.pausedMs && (
                              <span className="ml-1.5 text-[11px] text-amber-700 dark:text-amber-500" title="Paused time inside this stage, not counted">
                                {formatDurationMs(s.pausedMs)} paused
                              </span>
                            )}
                          </td>
                          <td className={cn('p-2 text-muted-foreground', s.estimated && 'italic')}>{s.started ? formatDateTime(s.started) : '—'}</td>
                          <td className={cn('p-2 text-muted-foreground', s.estimated && 'italic')}>{s.completed ? formatDateTime(s.completed) : '—'}</td>
                          <td className={cn('p-2 text-right font-mono font-medium', s.estimated && 'italic')}>{s.estimated ? `~${s.duration}` : s.duration}</td>
                        </tr>
                      ))}
                    </tbody>
                    {active.span.start && active.span.end && (
                      <tfoot>
                        <tr className="bg-muted/50">
                          <td colSpan={3} className="p-2 text-right font-medium">
                            {isMultiUnit ? `Unit ${activeKey} Duration:` : 'Total Duration:'}
                          </td>
                          <td className="p-2 text-right font-bold">
                            <BuildDurationValue duration={activeDuration} />
                            {activeDuration?.kind === 'tracked' && active.lunchMs > 0 && (
                              <span className="ml-1 font-normal font-sans text-xs text-muted-foreground">
                                ({Math.round(active.lunchMs / 3_600_000)}h lunch included)
                              </span>
                            )}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                  <PausedTimeNote duration={activeDuration} className="mt-2 text-right" />
                  <BuildDurationNote duration={activeDuration} className="mt-2 text-right" />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No stage data recorded for this unit.</p>
              )}

              <PauseReviewList
                order={order}
                pauses={order.pauses}
                canReview={false}
                activeDays={order.activeDays}
                className="mt-4"
              />
              <div className="mt-4">
                <BuildAttribution order={order} />
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* ─── 5. Per-Unit Benchmarks ────────────────────────────── */}
      {benchmarks && benchmarks.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
              PassMark Benchmark Scores
            </p>
            <div className={cn('grid gap-4', benchmarks.length > 1 ? 'md:grid-cols-2' : '')}>
              {benchmarks.map((b: any) => (
                <div key={b.id} className="p-3 bg-muted rounded-lg space-y-2">
                  {benchmarks.length > 1 && (
                    <p className="text-xs font-medium">Unit {b.unit_number}</p>
                  )}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    {([
                      ['passmark_rating', 'PassMark Rating'],
                      ['cpu_mark', 'CPU Mark'],
                      ['graphics_2d_mark', '2D Graphics'],
                      ['graphics_3d_mark', '3D Graphics'],
                      ['memory_mark', 'Memory'],
                      ['disk_mark', 'Disk'],
                    ] as const).map(([key, label]) => (
                      <div key={key} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{label}</span>
                        <span className="font-mono font-medium">{b[key] != null ? b[key].toLocaleString() : '—'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── 5b. Per-Unit Stress Tests ─────────────────────────── */}
      {/* Always rendered, unlike the benchmark card above it, because this is
          where a result gets attached as well as read. The sign-off dialog that
          normally captures it is technician-only, so without this an admin or
          manager could see a stress result but never record one. */}
      {order && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
              Stress Test Results
            </p>
            <div className={cn('grid gap-5', (order.quantity || 1) > 1 ? 'md:grid-cols-2' : '')}>
              {Array.from({ length: Math.max(order.quantity || 1, 1) }, (_, i) => (
                <StressTestUpload
                  key={i}
                  assemblyOrderId={order.id}
                  orderNumber={order.order_number}
                  unitNumber={i + 1}
                  // The order's technician, not whoever is uploading: the column
                  // records who ran the test, and an admin attaching a log after
                  // the fact did not run it.
                  technicianId={order.technician_id ?? null}
                  createdBy={currentUser?.name ?? currentUser?.email ?? null}
                />
              ))}
            </div>
            {stressTests.length === 0 && (
              <p className="text-xs text-muted-foreground mt-3">
                No stress test recorded yet. Upload the HWiNFO CSV log from the run.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ─── 6. Components (BOM) ───────────────────────────────── */}
      {components && components.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Components</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-2 font-medium">Type</th>
                    <th className="text-left p-2 font-medium">Part Number</th>
                    <th className="text-left p-2 font-medium">Description</th>
                    <th className="text-right p-2 font-medium">Qty</th>
                    {canViewCosts && <th className="text-right p-2 font-medium">Unit Cost</th>}
                    {canViewCosts && <th className="text-right p-2 font-medium">Line Total</th>}
                  </tr>
                </thead>
                <tbody>
                  {[...components].sort((a: any, b: any) => compSort(a.type) - compSort(b.type)).map((c: any, idx: number) => (
                    <tr key={idx} className="border-b last:border-0">
                      <td className="p-2">
                        {c.type && c.type !== 'COMPONENT' && (
                          <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-muted text-muted-foreground">
                            {getTypeLabel(c.type)}
                          </span>
                        )}
                      </td>
                      <td className="p-2 font-mono text-xs">{c.partNumber}</td>
                      <td className="p-2 text-xs truncate max-w-[250px]" title={c.description}>{c.description}</td>
                      <td className="p-2 text-right">{Number.isInteger(c.quantity) ? c.quantity : c.quantity.toFixed(1)}</td>
                      {canViewCosts && <td className="p-2 text-right font-mono">{formatCurrency(c.unitCost)}</td>}
                      {canViewCosts && <td className="p-2 text-right font-mono font-medium">{formatCurrency(c.lineTotal)}</td>}
                    </tr>
                  ))}
                </tbody>
                {canViewCosts && (
                  <tfoot>
                    <tr className="bg-muted/50">
                      <td colSpan={5} className="p-2 text-right font-medium">Build Cost:</td>
                      <td className="p-2 text-right font-mono font-bold">{formatCurrency(perBuildCost)}</td>
                    </tr>
                    {order.quantity > 1 && (
                      <tr className="bg-muted/50">
                        <td colSpan={5} className="p-2 text-right font-medium text-muted-foreground">
                          Total ({order.quantity} units):
                        </td>
                        <td className="p-2 text-right font-mono font-bold">{formatCurrency(order.total_cost_ex_vat)}</td>
                      </tr>
                    )}
                  </tfoot>
                )}
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── 7. Notes ──────────────────────────────────────────── */}
      {/* Inline technician notes (from order.notes field) */}
      {order.notes && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Technician Notes</p>
            <p className="text-sm p-3 bg-yellow-50 border border-yellow-200 rounded-md whitespace-pre-wrap">{order.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Reviewer's final remarks (from the review / posting dialog) */}
      {order.review_remarks && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Review Remarks</p>
            <p className="text-sm p-3 bg-blue-50 border border-blue-200 rounded-md whitespace-pre-wrap">{order.review_remarks}</p>
            {(order.review_remarks_author || order.review_remarks_at) && (
              <p className="text-xs text-muted-foreground mt-1">
                {order.review_remarks_author?.alias || order.review_remarks_author?.name || ''}
                {order.review_remarks_author && order.review_remarks_at && ' · '}
                {order.review_remarks_at && new Date(order.review_remarks_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* AO notes (from assembly_order_notes table) */}
      {notes && notes.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Notes</p>
            <div className="space-y-2">
              {notes.map((n: any) => (
                <div key={n.id} className="p-2 bg-muted rounded-md">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium">{n.author?.alias || n.author?.name || '—'}</span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(n.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{n.note_text}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── 8. Signatures ─────────────────────────────────────── */}
      {(order.tech_signature_url || order.manager_signature_url) && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Signatures</p>
            <div className="flex gap-8">
              {order.tech_signature_url && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Technician</p>
                  <img src={order.tech_signature_url} alt="Tech signature" className="border rounded-md max-h-20 bg-white" />
                </div>
              )}
              {order.manager_signature_url && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Manager / QC</p>
                  <img src={order.manager_signature_url} alt="Manager signature" className="border rounded-md max-h-20 bg-white" />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── 9. Audit Trail (collapsible) ─────────────────────── */}
      {auditLogs && auditLogs.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <button
              onClick={() => setAuditOpen(prev => !prev)}
              className="flex items-center gap-2 w-full text-left"
            >
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Audit Trail
              </p>
              <span className="text-xs text-muted-foreground">({auditLogs.length})</span>
              <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform ml-auto', auditOpen && 'rotate-180')} />
            </button>
            {auditOpen && (
              <div className="space-y-2 mt-3">
                {auditLogs.map((log: any) => (
                  <div key={log.id} className="flex items-start gap-3 text-sm p-2 bg-muted rounded-md">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{log.action.replace(/_/g, ' ')}</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(log.created_at).toLocaleString('en-GB', {
                            day: '2-digit', month: 'short', year: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          })}
                        </span>
                      </div>
                      {log.user_email && (
                        <p className="text-xs text-muted-foreground">{log.user_email}</p>
                      )}
                      {log.notes && (
                        <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-wrap">{log.notes}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
